import { create } from 'zustand';
import type { Color, GameAction, GameState, Move } from '@ccc/shared-types';
import {
  applyClockTick,
  chooseBotAction,
  createEngine,
  createInitialGameState,
  finishMoveClock,
  resetPieceIdCounter,
  startClocks,
  type BotDifficulty,
  type Engine,
} from '@ccc/rule-engine';
import {
  CARD_CATALOG,
  applyStartingSetupForCard,
  dealCards,
  modifiersForGame,
} from '@ccc/cards';

interface LocalGameStore {
  state: GameState | null;
  engine: Engine | null;
  draft: { whiteOffer: string[]; blackOffer: string[]; whitePicked: string | null; blackPicked: string | null } | null;
  initialSeconds: number;
  incrementSeconds: number;
  difficulty: BotDifficulty;
  startBot: (opts: { initialSeconds: number; incrementSeconds: number; difficulty: string }) => void;
  pickCard: (side: Color, cardId: string) => void;
  attemptAction: (action: GameAction) => boolean;
  attemptMove: (move: Move) => boolean;
  claimTimeout: () => void;
  reset: () => void;
}

export const useLocalGame = create<LocalGameStore>((set, get) => ({
  state: null,
  engine: null,
  draft: null,
  initialSeconds: 600,
  incrementSeconds: 0,
  difficulty: 'beginner',
  startBot(opts) {
    resetPieceIdCounter();
    const { white, black } = dealCards();
    set({
      draft: {
        whiteOffer: white.map((c) => c.id),
        blackOffer: black.map((c) => c.id),
        whitePicked: null,
        blackPicked: null,
      },
      initialSeconds: opts.initialSeconds,
      incrementSeconds: opts.incrementSeconds,
      difficulty: normalizeDifficulty(opts.difficulty),
    });
  },
  pickCard(side, cardId) {
    const draft = get().draft;
    if (!draft) return;
    const next = { ...draft, [side === 'white' ? 'whitePicked' : 'blackPicked']: cardId };
    set({ draft: next });

    // If both picked, transition to live.
    if (next.whitePicked && next.blackPicked) {
      const state = createInitialGameState({
        activeRules: { white: next.whitePicked, black: next.blackPicked },
        initialSeconds: get().initialSeconds,
        incrementSeconds: get().incrementSeconds,
      });
      state.status = 'active';
      applyStartingSetupForCard(state, next.whitePicked, 'white');
      applyStartingSetupForCard(state, next.blackPicked, 'black');
      startClocks(state);
      const engine = createEngine(
        modifiersForGame({ whiteCard: next.whitePicked, blackCard: next.blackPicked }),
      );
      set({ state, engine, draft: null });
    } else if (side === 'white' && !next.blackPicked) {
      // Bot picks: random from offer.
      setTimeout(() => {
        const d = get().draft;
        if (!d || d.blackPicked) return;
        const pick = d.blackOffer[Math.floor(Math.random() * d.blackOffer.length)]!;
        get().pickCard('black', pick);
      }, 600);
    }
  },
  attemptAction(action) {
    const { state, engine } = get();
    if (!state || !engine) return false;
    const now = Date.now();
    const timedState = structuredClone(state);
    if (applyClockTick(timedState, now)) {
      set({ state: timedState });
      return false;
    }
    const mover = timedState.turn;
    const v = engine.validateAction(timedState, action);
    if (!v.ok) return false;
    const r = engine.applyAction(timedState, action);
    finishMoveClock(r.state, mover, now);
    set({ state: r.state });
    if (!r.end && r.state.turn === 'black') {
      // Bot turn.
      setTimeout(() => {
        const cur = get();
        if (!cur.state || !cur.engine) return;
        const botNow = Date.now();
        const botState = structuredClone(cur.state);
        if (applyClockTick(botState, botNow)) {
          set({ state: botState });
          return;
        }
        const pick = chooseBotAction(cur.engine, botState, 'black', cur.difficulty);
        if (!pick) return;
        const r2 = cur.engine.applyAction(botState, pick);
        finishMoveClock(r2.state, 'black', botNow);
        set({ state: r2.state });
      }, 400);
    }
    return true;
  },
  attemptMove(move) {
    return get().attemptAction({ kind: 'move', move });
  },
  claimTimeout() {
    const state = get().state;
    if (!state || state.status !== 'active') return;
    const next = structuredClone(state);
    if (applyClockTick(next, Date.now())) set({ state: next });
  },
  reset() {
    set({ state: null, engine: null, draft: null });
  },
}));

/** Card data lookup. */
export function cardById(id: string) {
  return CARD_CATALOG.find((c) => c.id === id);
}

function normalizeDifficulty(value: string): BotDifficulty {
  if (value === 'easy' || value === 'medium' || value === 'hard' || value === 'adaptive') return value;
  return 'beginner';
}
